import { FormEvent, type ReactNode, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type { IncomingItem } from '@/features/masters/incoming-items/types';
import type { Material } from '@/features/masters/materials/types';
import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { listMaster, type MasterKind, type MasterListParams } from '@/lib/api-masters';
import type {
  MonthlyReportCommercialTermsDto,
  MonthlyReportProfileDto,
  MonthlyReportVariant,
} from './types';

const NS = 'reports';
const MONTHLY_REPORTS_KEY = 'monthly-reports' as const;

const CONFIGURABLE_VARIANTS: MonthlyReportVariant[] = [
  'external_operations',
  'customer_settlement',
  'processor_invoice',
  'internal_estimate',
];

const COMMERCIAL_VARIANTS: MonthlyReportVariant[] = [
  'customer_settlement',
  'processor_invoice',
  'internal_estimate',
];

const ROUNDING_MODES = ['round_half_up', 'round_half_even', 'floor', 'ceil'] as const;
type RoundingMode = (typeof ROUNDING_MODES)[number];

const RATE_PURPOSES = ['customer_settlement', 'processor_invoice'] as const;
const FEE_PURPOSES = RATE_PURPOSES;
const LOCALES = ['ja', 'zh-TW', 'en'] as const;
const UNIT_CHOICES = ['kg', 'package', 'trip'] as const;
const FEE_UNIT_CHOICES = ['kg', 'package', 'trip', 'fixed'] as const;
const FEE_CATEGORIES = ['processing', 'freight', 'management', 'system', 'other'] as const;
const QUANTITY_SOURCES = ['fixed', 'explicit', 'inbound_kg', 'produced_kg', 'trips'] as const;

const firstConfigurableVariant = (variant: MonthlyReportVariant): MonthlyReportVariant =>
  CONFIGURABLE_VARIANTS.includes(variant) ? variant : 'customer_settlement';

const firstCommercialVariant = (variant: MonthlyReportVariant): MonthlyReportVariant =>
  COMMERCIAL_VARIANTS.includes(variant) ? variant : 'customer_settlement';

const blankProfile = (variant: MonthlyReportVariant) => ({
  variant: firstConfigurableVariant(variant),
  effective_from: '',
  effective_to: '',
  output_locale: 'ja',
  issuer_name: '',
  issuer_address: '',
  issuer_phone: '',
  issuer_contact_name: '',
  recipient_display_rule: '',
  processing_method_display: '',
  remarks_template: '',
});

const blankIncomingRate = () => ({
  incoming_item_id: '',
  purpose: 'customer_settlement',
  unit: 'kg',
  unit_rate: '',
  source_note: '',
});

const blankProcessingFee = () => ({
  purpose: 'customer_settlement',
  fee_code: '',
  fee_category: '',
  quantity_source: '',
  quantity_value: '',
  unit: 'kg',
  unit_rate: '',
  fixed_amount: '',
  taxable: true,
  source_note: '',
});

const blankBenchmarkQuote = () => ({
  benchmark_label: '',
  incoming_item_id: '',
  material_id: '',
  unit: 'kg',
  quoted_unit_price: '',
  quote_source_date: '',
  source_note: '',
});

const blankTerms = (variant: MonthlyReportVariant) => ({
  variant: firstCommercialVariant(variant),
  recipient_company_id: '',
  currency_code: '',
  tax_rate: '',
  tax_rounding_mode: '',
  amount_rounding_mode: '',
  weight_rounding_mode: '',
  source_note: '',
  incoming_item_rates: [blankIncomingRate()],
  processing_fees: [blankProcessingFee()],
  benchmark_quotes: [blankBenchmarkQuote()],
});

type ProfileForm = ReturnType<typeof blankProfile>;
type TermsForm = ReturnType<typeof blankTerms>;
type IncomingRateForm = ReturnType<typeof blankIncomingRate>;
type ProcessingFeeForm = ReturnType<typeof blankProcessingFee>;
type BenchmarkQuoteForm = ReturnType<typeof blankBenchmarkQuote>;

interface MonthlyBatchForSettings {
  monthly_batch_id: string;
  facility_id: string;
  facility_name?: string;
  company_display_name?: string;
  year_month?: string;
}

interface CreateReportProfileRequest {
  platform_operator_id?: string | null;
  facility_id: string | null;
  supplier_company_id?: string | null;
  processor_company_id?: string | null;
  variant: MonthlyReportVariant;
  effective_from: string;
  effective_to: string | null;
  output_locale: string;
  issuer_name: string;
  issuer_address: string;
  issuer_phone: string | null;
  issuer_fax: string | null;
  issuer_contact_name: string | null;
  recipient_display_rule: string;
  processing_method_display: string | null;
  dismantling_processor_display: string | null;
  remarks_template: string | null;
  logo_company_file_id: string | null;
}

interface CreateCommercialTermSetRequest {
  platform_operator_id?: string | null;
  monthly_batch_id: string;
  facility_id: string | null;
  supplier_company_id?: string | null;
  processor_company_id?: string | null;
  recipient_company_id?: string | null;
  variant: MonthlyReportVariant;
  effective_from: string | null;
  effective_to: string | null;
  currency_code: string;
  tax_rate: string;
  tax_rounding_mode: string;
  amount_rounding_mode: string;
  weight_rounding_mode: string;
  source_note: string | null;
  incoming_item_rates: Array<{
    incoming_item_id: string;
    purpose: string;
    unit: string;
    unit_rate: string;
    source_note: string | null;
  }>;
  processing_fees: Array<{
    purpose: string;
    fee_code: string;
    fee_category: string;
    quantity_source: string;
    quantity_value: string | null;
    unit: string;
    unit_rate: string | null;
    fixed_amount: string | null;
    taxable: boolean;
    source_note: string | null;
  }>;
  benchmark_quotes: Array<{
    benchmark_company_id: string | null;
    benchmark_label: string;
    incoming_item_id: string | null;
    material_id: string | null;
    unit: string;
    quoted_unit_price: string;
    quote_source_date: string;
    source_note: string | null;
  }>;
}

interface ReportSettingsCreatePanelProps {
  batchId: string;
  variant: MonthlyReportVariant;
  canManageProfiles: boolean;
  canManageTerms: boolean;
}

const fetchBatchForSettings = async (batchId: string): Promise<MonthlyBatchForSettings> => {
  const res = await apiClient.get<ApiSuccess<MonthlyBatchForSettings>>(
    `/monthly-batches/${batchId}`,
  );
  return res.data.data;
};

const createReportProfile = async (
  batchId: string,
  request: CreateReportProfileRequest,
): Promise<MonthlyReportProfileDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportProfileDto>>(
    `/monthly-batches/${batchId}/report-settings/profiles`,
    request,
  );
  return res.data.data;
};

const createCommercialTerms = async (
  batchId: string,
  request: CreateCommercialTermSetRequest,
): Promise<MonthlyReportCommercialTermsDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportCommercialTermsDto>>(
    `/monthly-batches/${batchId}/report-settings/commercial-terms`,
    request,
  );
  return res.data.data;
};

const clean = (value: string): string => value.trim();
const optional = (value: string): string | null => {
  const trimmed = clean(value);
  return trimmed === '' ? null : trimmed;
};

const hasIncomingRateInput = (line: IncomingRateForm): boolean =>
  line.incoming_item_id !== '' ||
  line.unit_rate !== '' ||
  line.source_note !== '' ||
  line.purpose !== blankIncomingRate().purpose ||
  line.unit !== blankIncomingRate().unit;

const hasProcessingFeeInput = (line: ProcessingFeeForm): boolean =>
  line.purpose !== blankProcessingFee().purpose ||
  line.fee_code !== '' ||
  line.fee_category !== '' ||
  line.quantity_source !== '' ||
  line.quantity_value !== '' ||
  line.unit !== blankProcessingFee().unit ||
  line.unit_rate !== '' ||
  line.fixed_amount !== '' ||
  line.taxable !== blankProcessingFee().taxable ||
  line.source_note !== '';

const hasBenchmarkQuoteInput = (line: BenchmarkQuoteForm): boolean =>
  line.benchmark_label !== '' ||
  line.incoming_item_id !== '' ||
  line.material_id !== '' ||
  line.unit !== blankBenchmarkQuote().unit ||
  line.quoted_unit_price !== '' ||
  line.quote_source_date !== '' ||
  line.source_note !== '';

const formatOptionLabel = (item: IncomingItem | Material): string => {
  const normalized = item.normalized_name ? ` / ${item.normalized_name}` : '';
  return `${item.japanese_name}${normalized}`;
};

const listAllMasterRows = async <T,>(kind: MasterKind, params: MasterListParams): Promise<T[]> => {
  const rows: T[] = [];
  let page = 1;
  const perPage = 100;
  for (;;) {
    const result = await listMaster<T>(kind, { ...params, page, per_page: perPage });
    rows.push(...result.rows);
    if (rows.length >= result.meta.total || result.rows.length === 0) return rows;
    page += 1;
  }
};

interface ReportRecipientOption {
  company_id: string;
  display_name: string;
  legal_name: string;
}

const listBatchSuppliers = async (batchId: string): Promise<ReportRecipientOption[]> => {
  const res = await apiClient.get<ApiSuccess<ReportRecipientOption[]>>(
    `/monthly-batches/${batchId}/report-settings/recipient-options`,
  );
  return res.data.data;
};

export const ReportSettingsCreatePanel = ({
  batchId,
  variant,
  canManageProfiles,
  canManageTerms,
}: ReportSettingsCreatePanelProps) => {
  const { t } = useTranslation(NS);
  const queryClient = useQueryClient();
  const batchQuery = useQuery<MonthlyBatchForSettings, ApiClientError>({
    queryKey: [MONTHLY_REPORTS_KEY, 'settings', 'batch-scope', batchId],
    queryFn: () => fetchBatchForSettings(batchId),
    enabled: !!batchId,
  });
  const incomingItemsQuery = useQuery<IncomingItem[], ApiClientError>({
    queryKey: [MONTHLY_REPORTS_KEY, 'settings', 'masters', 'incoming-items'],
    queryFn: () =>
      listAllMasterRows<IncomingItem>('incoming-items', { status: 'approved', active: true }),
    enabled: canManageTerms && variant !== 'external_operations',
  });
  const materialsQuery = useQuery<Material[], ApiClientError>({
    queryKey: [MONTHLY_REPORTS_KEY, 'settings', 'masters', 'materials'],
    queryFn: () => listAllMasterRows<Material>('materials', { status: 'approved', active: true }),
    enabled: canManageTerms && variant !== 'external_operations',
  });
  const suppliersQuery = useQuery<ReportRecipientOption[], ApiClientError>({
    queryKey: [MONTHLY_REPORTS_KEY, 'settings', 'suppliers', batchId],
    queryFn: () => listBatchSuppliers(batchId),
    enabled: canManageTerms && variant !== 'external_operations',
  });

  const [profileForm, setProfileForm] = useState<ProfileForm>(() => blankProfile(variant));
  const [termsForm, setTermsForm] = useState<TermsForm>(() => blankTerms(variant));
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [termsMessage, setTermsMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  const incomingItemOptions = useMemo<SearchableSelectOption[]>(
    () =>
      (incomingItemsQuery.data ?? []).map((item) => ({
        value: item.incoming_item_id,
        label: formatOptionLabel(item),
        keywords: `${item.category_group} ${item.normalized_name}`,
      })),
    [incomingItemsQuery.data],
  );

  const materialOptions = useMemo<SearchableSelectOption[]>(
    () =>
      (materialsQuery.data ?? []).map((material) => ({
        value: material.material_id,
        label: formatOptionLabel(material),
        keywords: `${material.material_type} ${material.normalized_name}`,
      })),
    [materialsQuery.data],
  );

  const supplierOptions = useMemo(
    () =>
      (suppliersQuery.data ?? []).map((company) => ({
        value: company.company_id,
        label: company.display_name || company.legal_name,
      })),
    [suppliersQuery.data],
  );

  const invalidateSettings = async (createdVariant: MonthlyReportVariant) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [MONTHLY_REPORTS_KEY, 'settings'] }),
      queryClient.invalidateQueries({
        queryKey: [MONTHLY_REPORTS_KEY, 'artifact-precheck', batchId],
      }),
      queryClient.invalidateQueries({
        queryKey: [MONTHLY_REPORTS_KEY, 'artifacts', batchId, createdVariant],
      }),
    ]);
  };

  const profileMutation = useMutation<
    MonthlyReportProfileDto,
    ApiClientError,
    CreateReportProfileRequest
  >({
    mutationFn: (request) => createReportProfile(batchId, request),
    onSuccess: async (profile) => {
      await invalidateSettings(profile.variant as MonthlyReportVariant);
      setProfileError(null);
      setProfileMessage(
        t('efs.reports.monthly.setup.success_profile', {
          defaultValue:
            'Profile draft created: v{{version}} ({{issuer}}). Lock it before generation.',
          version: profile.version,
          issuer: profile.issuer_name,
        }),
      );
      setProfileForm(blankProfile(profile.variant as MonthlyReportVariant));
    },
    onError: () => {
      setProfileMessage(null);
      setProfileError(null);
    },
  });

  const termsMutation = useMutation<
    MonthlyReportCommercialTermsDto,
    ApiClientError,
    CreateCommercialTermSetRequest
  >({
    mutationFn: (request) => createCommercialTerms(batchId, request),
    onSuccess: async (terms) => {
      await invalidateSettings(terms.term_set.variant as MonthlyReportVariant);
      setTermsError(null);
      setTermsMessage(
        t('efs.reports.monthly.setup.success_terms', {
          defaultValue:
            'Commercial terms draft created: v{{version}} ({{currency}}). Lock it before generation.',
          version: terms.term_set.version,
          currency: terms.term_set.currency_code,
        }),
      );
      setTermsForm(blankTerms(terms.term_set.variant as MonthlyReportVariant));
    },
    onError: () => {
      setTermsMessage(null);
      setTermsError(null);
    },
  });

  const batch = batchQuery.data;

  const submitProfile = (event: FormEvent) => {
    event.preventDefault();
    if (!batch?.facility_id) {
      setProfileError(
        t('efs.reports.monthly.setup.batch_scope_unavailable', {
          defaultValue: 'Batch scope is unavailable; reload the batch and retry.',
        }),
      );
      return;
    }
    const requiredValues = [
      profileForm.effective_from,
      profileForm.output_locale,
      profileForm.issuer_name,
      profileForm.issuer_address,
      profileForm.recipient_display_rule,
    ];
    if (requiredValues.some((value) => clean(value) === '')) {
      setProfileError(
        t('efs.reports.monthly.setup.validation_required', {
          defaultValue: 'Fill all required fields before creating the draft.',
        }),
      );
      return;
    }
    // Optional end date: without it the profile stays open-ended and blocks any
    // later profile of the same report type for this facility.
    if (profileForm.effective_to && profileForm.effective_to < profileForm.effective_from) {
      setProfileError(
        t('efs.reports.monthly.setup.validation_effective_range', {
          defaultValue: 'The end date must be on or after the start date.',
        }),
      );
      return;
    }
    setProfileError(null);
    profileMutation.mutate({
      facility_id: batch.facility_id,
      supplier_company_id: null,
      processor_company_id: null,
      variant: profileForm.variant,
      effective_from: profileForm.effective_from,
      effective_to: profileForm.effective_to || null,
      output_locale: profileForm.output_locale,
      issuer_name: clean(profileForm.issuer_name),
      issuer_address: clean(profileForm.issuer_address),
      issuer_phone: optional(profileForm.issuer_phone),
      issuer_fax: null,
      issuer_contact_name: optional(profileForm.issuer_contact_name),
      recipient_display_rule: clean(profileForm.recipient_display_rule),
      processing_method_display: optional(profileForm.processing_method_display),
      dismantling_processor_display: null,
      remarks_template: optional(profileForm.remarks_template),
      logo_company_file_id: null,
    });
  };

  const submitTerms = (event: FormEvent) => {
    event.preventDefault();
    if (!batch?.facility_id) {
      setTermsError(
        t('efs.reports.monthly.setup.batch_scope_unavailable', {
          defaultValue: 'Batch scope is unavailable; reload the batch and retry.',
        }),
      );
      return;
    }
    if (
      !termsForm.currency_code.trim() ||
      !termsForm.tax_rate.trim() ||
      !termsForm.tax_rounding_mode ||
      !termsForm.amount_rounding_mode ||
      !termsForm.weight_rounding_mode ||
      (termsForm.variant === 'customer_settlement' && !termsForm.recipient_company_id)
    ) {
      setTermsError(
        t('efs.reports.monthly.setup.validation_required', {
          defaultValue: 'Fill all required fields before creating the draft.',
        }),
      );
      return;
    }
    const incoming_item_rates = termsForm.incoming_item_rates
      .filter(hasIncomingRateInput)
      .map((line) => ({
        incoming_item_id: line.incoming_item_id,
        purpose: line.purpose,
        unit: clean(line.unit),
        unit_rate: clean(line.unit_rate),
        source_note: optional(line.source_note),
      }));
    const processing_fees = termsForm.processing_fees.filter(hasProcessingFeeInput).map((line) => ({
      purpose: line.purpose,
      fee_code: clean(line.fee_code),
      fee_category: clean(line.fee_category),
      quantity_source: clean(line.quantity_source),
      quantity_value: optional(line.quantity_value),
      unit: clean(line.unit),
      unit_rate: optional(line.unit_rate),
      fixed_amount: optional(line.fixed_amount),
      taxable: line.taxable,
      source_note: optional(line.source_note),
    }));
    const benchmark_quotes = termsForm.benchmark_quotes
      .filter(hasBenchmarkQuoteInput)
      .map((line) => ({
        benchmark_company_id: null,
        benchmark_label: clean(line.benchmark_label),
        incoming_item_id: optional(line.incoming_item_id),
        material_id: optional(line.material_id),
        unit: clean(line.unit),
        quoted_unit_price: clean(line.quoted_unit_price),
        quote_source_date: line.quote_source_date,
        source_note: optional(line.source_note),
      }));
    if (
      incoming_item_rates.some((line) => !line.incoming_item_id || !line.unit || !line.unit_rate) ||
      processing_fees.some(
        (line) =>
          !line.purpose ||
          !line.fee_code ||
          !line.fee_category ||
          !line.quantity_source ||
          !line.unit ||
          (!line.unit_rate && !line.fixed_amount),
      ) ||
      benchmark_quotes.some(
        (line) =>
          !line.benchmark_label || !line.unit || !line.quoted_unit_price || !line.quote_source_date,
      )
    ) {
      setTermsError(
        t('efs.reports.monthly.setup.validation_required', {
          defaultValue: 'Fill all required fields before creating the draft.',
        }),
      );
      return;
    }
    setTermsError(null);
    termsMutation.mutate({
      monthly_batch_id: batchId,
      facility_id: batch.facility_id,
      supplier_company_id:
        termsForm.variant === 'customer_settlement' ? termsForm.recipient_company_id : null,
      processor_company_id: null,
      recipient_company_id:
        termsForm.variant === 'customer_settlement' ? termsForm.recipient_company_id : null,
      variant: termsForm.variant,
      effective_from: null,
      effective_to: null,
      currency_code: clean(termsForm.currency_code).toUpperCase(),
      tax_rate: clean(termsForm.tax_rate),
      tax_rounding_mode: termsForm.tax_rounding_mode,
      amount_rounding_mode: termsForm.amount_rounding_mode,
      weight_rounding_mode: termsForm.weight_rounding_mode,
      source_note: optional(termsForm.source_note),
      incoming_item_rates,
      processing_fees,
      benchmark_quotes,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('efs.reports.monthly.setup.panel_title', {
            defaultValue: 'Create report settings',
          })}
        </CardTitle>
        <p className="text-sm text-efs-text-muted">
          {t('efs.reports.monthly.setup.panel_description', {
            defaultValue:
              'Create draft report details, rates and fees for this monthly summary sheet. Review and lock each draft before generating a report.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm">
          <p className="font-medium text-efs-text-primary">
            {t('efs.reports.monthly.setup.scope_section', { defaultValue: 'Batch scope' })}
          </p>
          {batchQuery.isLoading ? (
            <p className="mt-1 flex items-center gap-2 text-efs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('efs.reports.monthly.setup.batch_scope_loading', {
                defaultValue: 'Loading batch scope…',
              })}
            </p>
          ) : batchQuery.error ? (
            <p className="mt-1 text-destructive">
              {t('efs.reports.monthly.setup.batch_scope_unavailable', {
                defaultValue: 'Batch scope is unavailable; reload the batch and retry.',
              })}
            </p>
          ) : (
            <p className="mt-1 text-efs-text-muted">
              {batch?.facility_name ?? batch?.facility_id ?? '-'}
              {batch?.company_display_name ? ` / ${batch.company_display_name}` : ''}
              {batch?.year_month ? ` / ${batch.year_month}` : ''}
            </p>
          )}
        </div>

        <form className="space-y-4" onSubmit={submitProfile}>
          <SectionTitle
            title={t('efs.reports.monthly.setup.profile_section', {
              defaultValue: 'Profile draft',
            })}
          />
          {!canManageProfiles ? (
            <Notice
              text={t('efs.reports.monthly.setup.not_allowed_profile', {
                defaultValue: 'You do not have permission to create profile drafts.',
              })}
            />
          ) : null}
          {profileMessage ? <SuccessNotice text={profileMessage} /> : null}
          {profileError ? (
            <ErrorNotice
              title={t('efs.reports.monthly.setup.error_title', {
                defaultValue: 'Could not create settings',
              })}
              text={profileError}
            />
          ) : null}
          {profileMutation.error ? <LocalizedApiError error={profileMutation.error} /> : null}
          <div className="grid gap-3 md:grid-cols-2">
            <SelectField
              label={t('efs.reports.monthly.setup.variant_label', {
                defaultValue: 'Report variant',
              })}
              value={profileForm.variant}
              onValueChange={(next) =>
                setProfileForm({ ...profileForm, variant: next as MonthlyReportVariant })
              }
              disabled={!canManageProfiles || profileMutation.isPending}
              options={CONFIGURABLE_VARIANTS.map((item) => ({
                value: item,
                label: t(`efs.reports.monthly.setup.variant_${item}`, { defaultValue: item }),
              }))}
            />
            <SelectField
              label={t('efs.reports.monthly.setup.output_locale', {
                defaultValue: 'Output locale',
              })}
              value={profileForm.output_locale}
              onValueChange={(next) => setProfileForm({ ...profileForm, output_locale: next })}
              disabled={!canManageProfiles || profileMutation.isPending}
              options={LOCALES.map((item) => ({
                value: item,
                label: t(`efs.reports.monthly.setup.locale_${item}`, { defaultValue: item }),
              }))}
            />
            <TextField
              label={t('efs.reports.monthly.setup.effective_from', {
                defaultValue: 'Effective from',
              })}
              type="date"
              value={profileForm.effective_from}
              onChange={(value) => setProfileForm({ ...profileForm, effective_from: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
              required
            />
            <TextField
              label={t('efs.reports.monthly.setup.effective_to', {
                defaultValue: 'Effective to (optional)',
              })}
              type="date"
              value={profileForm.effective_to}
              onChange={(value) => setProfileForm({ ...profileForm, effective_to: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
            />
            <TextField
              label={t('efs.reports.monthly.setup.issuer_name', { defaultValue: 'Issuer name' })}
              value={profileForm.issuer_name}
              onChange={(value) => setProfileForm({ ...profileForm, issuer_name: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
              required
            />
            <TextField
              label={t('efs.reports.monthly.setup.issuer_phone', { defaultValue: 'Issuer phone' })}
              value={profileForm.issuer_phone}
              onChange={(value) => setProfileForm({ ...profileForm, issuer_phone: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
            />
            <TextField
              label={t('efs.reports.monthly.setup.issuer_contact_name', {
                defaultValue: 'Issuer contact',
              })}
              value={profileForm.issuer_contact_name}
              onChange={(value) => setProfileForm({ ...profileForm, issuer_contact_name: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
            />
            <TextAreaField
              className="md:col-span-2"
              label={t('efs.reports.monthly.setup.issuer_address', {
                defaultValue: 'Issuer address',
              })}
              value={profileForm.issuer_address}
              onChange={(value) => setProfileForm({ ...profileForm, issuer_address: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
              required
            />
            <TextAreaField
              className="md:col-span-2"
              label={t('efs.reports.monthly.setup.recipient_display_rule', {
                defaultValue: 'Recipient display override',
              })}
              value={profileForm.recipient_display_rule}
              onChange={(value) =>
                setProfileForm({ ...profileForm, recipient_display_rule: value })
              }
              disabled={!canManageProfiles || profileMutation.isPending}
              required
            />
            <TextAreaField
              className="md:col-span-2"
              label={t('efs.reports.monthly.setup.processing_method_display', {
                defaultValue: 'Processing text',
              })}
              value={profileForm.processing_method_display}
              onChange={(value) =>
                setProfileForm({ ...profileForm, processing_method_display: value })
              }
              disabled={!canManageProfiles || profileMutation.isPending}
            />
            <TextAreaField
              className="md:col-span-2"
              label={t('efs.reports.monthly.setup.remarks_template', {
                defaultValue: 'Remarks template',
              })}
              value={profileForm.remarks_template}
              onChange={(value) => setProfileForm({ ...profileForm, remarks_template: value })}
              disabled={!canManageProfiles || profileMutation.isPending}
            />
          </div>
          <Button
            type="submit"
            disabled={!canManageProfiles || profileMutation.isPending || batchQuery.isLoading}
          >
            {profileMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('efs.reports.monthly.setup.submit_profile', {
              defaultValue: 'Create profile draft',
            })}
          </Button>
        </form>

        {variant === 'external_operations' ? null : (
          <form className="space-y-4" onSubmit={submitTerms}>
            <SectionTitle
              title={t('efs.reports.monthly.setup.terms_section', {
                defaultValue: 'Commercial terms draft',
              })}
            />
            {!canManageTerms ? (
              <Notice
                text={t('efs.reports.monthly.setup.not_allowed_terms', {
                  defaultValue: 'You do not have permission to create commercial terms.',
                })}
              />
            ) : null}
            {termsMessage ? <SuccessNotice text={termsMessage} /> : null}
            {termsError ? (
              <ErrorNotice
                title={t('efs.reports.monthly.setup.error_title', {
                  defaultValue: 'Could not create settings',
                })}
                text={termsError}
              />
            ) : null}
            {termsMutation.error ? <LocalizedApiError error={termsMutation.error} /> : null}
            {incomingItemsQuery.error ? (
              <LocalizedApiError error={incomingItemsQuery.error} />
            ) : null}
            {materialsQuery.error ? <LocalizedApiError error={materialsQuery.error} /> : null}
            {suppliersQuery.error ? <LocalizedApiError error={suppliersQuery.error} /> : null}
            <div className="grid gap-3 md:grid-cols-3">
              <SelectField
                label={t('efs.reports.monthly.setup.variant_label', {
                  defaultValue: 'Report variant',
                })}
                value={termsForm.variant}
                onValueChange={(next) =>
                  setTermsForm({ ...termsForm, variant: next as MonthlyReportVariant })
                }
                disabled={!canManageTerms || termsMutation.isPending}
                options={COMMERCIAL_VARIANTS.map((item) => ({
                  value: item,
                  label: t(`efs.reports.monthly.setup.variant_${item}`, { defaultValue: item }),
                }))}
              />
              {termsForm.variant === 'customer_settlement' ? (
                <SelectField
                  label={t('efs.reports.monthly.setup.recipient_company', {
                    defaultValue: 'Recipient supplier',
                  })}
                  value={termsForm.recipient_company_id}
                  onValueChange={(next) =>
                    setTermsForm({ ...termsForm, recipient_company_id: next })
                  }
                  disabled={!canManageTerms || termsMutation.isPending || suppliersQuery.isLoading}
                  placeholder={t('efs.reports.monthly.setup.no_option', {
                    defaultValue: 'Select an option',
                  })}
                  options={supplierOptions}
                />
              ) : null}
              <TextField
                label={t('efs.reports.monthly.setup.currency_code', {
                  defaultValue: 'Currency code',
                })}
                value={termsForm.currency_code}
                onChange={(value) =>
                  setTermsForm({ ...termsForm, currency_code: value.toUpperCase() })
                }
                disabled={!canManageTerms || termsMutation.isPending}
                required
              />
              <TextField
                label={t('efs.reports.monthly.setup.tax_rate', { defaultValue: 'Tax rate' })}
                value={termsForm.tax_rate}
                onChange={(value) => setTermsForm({ ...termsForm, tax_rate: value })}
                disabled={!canManageTerms || termsMutation.isPending}
                required
              />
              <SelectField
                label={t('efs.reports.monthly.setup.tax_rounding_mode', {
                  defaultValue: 'Tax rounding',
                })}
                value={termsForm.tax_rounding_mode}
                onValueChange={(next) =>
                  setTermsForm({ ...termsForm, tax_rounding_mode: next as RoundingMode })
                }
                disabled={!canManageTerms || termsMutation.isPending}
                placeholder={t('efs.reports.monthly.setup.no_option', {
                  defaultValue: 'Select an option',
                })}
                options={ROUNDING_MODES.map((item) => ({
                  value: item,
                  label: t(`efs.reports.monthly.setup.rounding_${item}`, { defaultValue: item }),
                }))}
              />
              <SelectField
                label={t('efs.reports.monthly.setup.amount_rounding_mode', {
                  defaultValue: 'Amount rounding',
                })}
                value={termsForm.amount_rounding_mode}
                onValueChange={(next) =>
                  setTermsForm({ ...termsForm, amount_rounding_mode: next as RoundingMode })
                }
                disabled={!canManageTerms || termsMutation.isPending}
                placeholder={t('efs.reports.monthly.setup.no_option', {
                  defaultValue: 'Select an option',
                })}
                options={ROUNDING_MODES.map((item) => ({
                  value: item,
                  label: t(`efs.reports.monthly.setup.rounding_${item}`, { defaultValue: item }),
                }))}
              />
              <SelectField
                label={t('efs.reports.monthly.setup.weight_rounding_mode', {
                  defaultValue: 'Weight rounding',
                })}
                value={termsForm.weight_rounding_mode}
                onValueChange={(next) =>
                  setTermsForm({ ...termsForm, weight_rounding_mode: next as RoundingMode })
                }
                disabled={!canManageTerms || termsMutation.isPending}
                placeholder={t('efs.reports.monthly.setup.no_option', {
                  defaultValue: 'Select an option',
                })}
                options={ROUNDING_MODES.map((item) => ({
                  value: item,
                  label: t(`efs.reports.monthly.setup.rounding_${item}`, { defaultValue: item }),
                }))}
              />
              <TextAreaField
                className="md:col-span-3"
                label={t('efs.reports.monthly.setup.source_note', { defaultValue: 'Source note' })}
                value={termsForm.source_note}
                onChange={(value) => setTermsForm({ ...termsForm, source_note: value })}
                disabled={!canManageTerms || termsMutation.isPending}
              />
            </div>

            <IncomingRateRows
              rows={termsForm.incoming_item_rates}
              options={incomingItemOptions}
              disabled={!canManageTerms || termsMutation.isPending}
              onChange={(incoming_item_rates) =>
                setTermsForm({ ...termsForm, incoming_item_rates })
              }
            />
            <ProcessingFeeRows
              rows={termsForm.processing_fees}
              disabled={!canManageTerms || termsMutation.isPending}
              onChange={(processing_fees) => setTermsForm({ ...termsForm, processing_fees })}
            />
            <BenchmarkQuoteRows
              rows={termsForm.benchmark_quotes}
              incomingItemOptions={incomingItemOptions}
              materialOptions={materialOptions}
              disabled={!canManageTerms || termsMutation.isPending}
              onChange={(benchmark_quotes) => setTermsForm({ ...termsForm, benchmark_quotes })}
            />

            <Button
              type="submit"
              disabled={!canManageTerms || termsMutation.isPending || batchQuery.isLoading}
            >
              {termsMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('efs.reports.monthly.setup.submit_terms', {
                defaultValue: 'Create commercial terms draft',
              })}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

const SectionTitle = ({ title }: { title: string }) => (
  <h3 className="border-b border-efs-border pb-2 text-sm font-semibold text-efs-text-primary">
    {title}
  </h3>
);

const Notice = ({ text }: { text: string }) => (
  <Alert>
    <AlertDescription>{text}</AlertDescription>
  </Alert>
);

const SuccessNotice = ({ text }: { text: string }) => (
  <Alert className="border-emerald-500/40">
    <AlertDescription>{text}</AlertDescription>
  </Alert>
);

const ErrorNotice = ({ title, text }: { title: string; text: string }) => (
  <Alert variant="destructive">
    <AlertTitle>{title}</AlertTitle>
    <AlertDescription>{text}</AlertDescription>
  </Alert>
);

const TextField = ({
  label,
  value,
  onChange,
  disabled,
  required,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  type?: string;
}) => (
  <Label className="block space-y-1">
    <span>{label}</span>
    <Input
      type={type}
      value={value}
      required={required}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  </Label>
);

const TextAreaField = ({
  label,
  value,
  onChange,
  disabled,
  required,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}) => (
  <Label className={`block space-y-1 ${className ?? ''}`}>
    <span>{label}</span>
    <Textarea
      value={value}
      required={required}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  </Label>
);

const SelectField = ({
  label,
  value,
  onValueChange,
  options,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  placeholder?: string;
}) => (
  <Label className="block space-y-1">
    <span>{label}</span>
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Label>
);

const RowControls = ({
  addLabel,
  onAdd,
  disabled,
}: {
  addLabel: string;
  onAdd: () => void;
  disabled?: boolean;
}) => (
  <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={disabled}>
    <Plus className="mr-2 h-4 w-4" />
    {addLabel}
  </Button>
);

const IncomingRateRows = ({
  rows,
  options,
  disabled,
  onChange,
}: {
  rows: IncomingRateForm[];
  options: SearchableSelectOption[];
  disabled?: boolean;
  onChange: (rows: IncomingRateForm[]) => void;
}) => {
  const { t } = useTranslation(NS);
  const update = (index: number, patch: Partial<IncomingRateForm>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <LineSection
      title={t('efs.reports.monthly.setup.incoming_rates', { defaultValue: 'Incoming item rates' })}
      addLabel={t('efs.reports.monthly.setup.add_row', { defaultValue: 'Add row' })}
      onAdd={() => onChange([...rows, blankIncomingRate()])}
      disabled={disabled}
    >
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid gap-2 rounded-md border border-efs-border p-3 md:grid-cols-5"
        >
          <SearchField
            label={t('efs.reports.monthly.setup.incoming_item', { defaultValue: 'Incoming item' })}
            value={row.incoming_item_id}
            options={options}
            disabled={disabled}
            onChange={(value) => update(index, { incoming_item_id: value })}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.purpose', { defaultValue: 'Purpose' })}
            value={row.purpose}
            disabled={disabled}
            onValueChange={(value) => update(index, { purpose: value })}
            options={RATE_PURPOSES.map((purpose) => ({
              value: purpose,
              label: t(`efs.reports.monthly.setup.purpose_${purpose}`, { defaultValue: purpose }),
            }))}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.unit', { defaultValue: 'Unit' })}
            value={row.unit}
            disabled={disabled}
            onValueChange={(value) => update(index, { unit: value })}
            options={UNIT_CHOICES.map((unit) => ({
              value: unit,
              label: t(`efs.reports.monthly.setup.unit_${unit}`, { defaultValue: unit }),
            }))}
          />
          <TextField
            label={t('efs.reports.monthly.setup.unit_rate', { defaultValue: 'Unit rate' })}
            value={row.unit_rate}
            disabled={disabled}
            onChange={(value) => update(index, { unit_rate: value })}
          />
          <RemoveButton
            disabled={disabled || rows.length <= 1}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
        </div>
      ))}
    </LineSection>
  );
};

const ProcessingFeeRows = ({
  rows,
  disabled,
  onChange,
}: {
  rows: ProcessingFeeForm[];
  disabled?: boolean;
  onChange: (rows: ProcessingFeeForm[]) => void;
}) => {
  const { t } = useTranslation(NS);
  const update = (index: number, patch: Partial<ProcessingFeeForm>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <LineSection
      title={t('efs.reports.monthly.setup.processing_fees', {
        defaultValue: 'Processing fee rows',
      })}
      addLabel={t('efs.reports.monthly.setup.add_row', { defaultValue: 'Add row' })}
      onAdd={() => onChange([...rows, blankProcessingFee()])}
      disabled={disabled}
    >
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid gap-2 rounded-md border border-efs-border p-3 md:grid-cols-6"
        >
          <SelectField
            label={t('efs.reports.monthly.setup.purpose', { defaultValue: 'Purpose' })}
            value={row.purpose}
            disabled={disabled}
            onValueChange={(value) => update(index, { purpose: value })}
            options={FEE_PURPOSES.map((purpose) => ({
              value: purpose,
              label: t(`efs.reports.monthly.setup.purpose_${purpose}`, { defaultValue: purpose }),
            }))}
          />
          <TextField
            label={t('efs.reports.monthly.setup.fee_code', { defaultValue: 'Fee code' })}
            value={row.fee_code}
            disabled={disabled}
            onChange={(value) => update(index, { fee_code: value })}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.fee_category', { defaultValue: 'Fee category' })}
            value={row.fee_category}
            disabled={disabled}
            placeholder={t('efs.reports.monthly.setup.no_option', {
              defaultValue: 'Select an option',
            })}
            onValueChange={(value) => update(index, { fee_category: value })}
            options={FEE_CATEGORIES.map((category) => ({
              value: category,
              label: t(`efs.reports.monthly.setup.fee_category_${category}`, {
                defaultValue: category,
              }),
            }))}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.quantity_source', {
              defaultValue: 'Quantity source',
            })}
            value={row.quantity_source}
            disabled={disabled}
            placeholder={t('efs.reports.monthly.setup.no_option', {
              defaultValue: 'Select an option',
            })}
            onValueChange={(value) => update(index, { quantity_source: value })}
            options={QUANTITY_SOURCES.map((source) => ({
              value: source,
              label: t(`efs.reports.monthly.setup.quantity_source_${source}`, {
                defaultValue: source,
              }),
            }))}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.unit', { defaultValue: 'Unit' })}
            value={row.unit}
            disabled={disabled}
            onValueChange={(value) => update(index, { unit: value })}
            options={FEE_UNIT_CHOICES.map((unit) => ({
              value: unit,
              label: t(`efs.reports.monthly.setup.unit_${unit}`, { defaultValue: unit }),
            }))}
          />
          <RemoveButton
            disabled={disabled || rows.length <= 1}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
          <TextField
            label={t('efs.reports.monthly.setup.quantity_value', {
              defaultValue: 'Quantity value',
            })}
            value={row.quantity_value}
            disabled={disabled}
            onChange={(value) => update(index, { quantity_value: value })}
          />
          <TextField
            label={t('efs.reports.monthly.setup.unit_rate', { defaultValue: 'Unit rate' })}
            value={row.unit_rate}
            disabled={disabled}
            onChange={(value) => update(index, { unit_rate: value })}
          />
          <TextField
            label={t('efs.reports.monthly.setup.fixed_amount', { defaultValue: 'Fixed amount' })}
            value={row.fixed_amount}
            disabled={disabled}
            onChange={(value) => update(index, { fixed_amount: value })}
          />
          <Label className="flex items-center gap-2 pt-6 text-sm">
            <input
              type="checkbox"
              checked={row.taxable}
              disabled={disabled}
              onChange={(event) => update(index, { taxable: event.target.checked })}
            />
            {t('efs.reports.monthly.setup.taxable', { defaultValue: 'Taxable' })}
          </Label>
        </div>
      ))}
    </LineSection>
  );
};

const BenchmarkQuoteRows = ({
  rows,
  incomingItemOptions,
  materialOptions,
  disabled,
  onChange,
}: {
  rows: BenchmarkQuoteForm[];
  incomingItemOptions: SearchableSelectOption[];
  materialOptions: SearchableSelectOption[];
  disabled?: boolean;
  onChange: (rows: BenchmarkQuoteForm[]) => void;
}) => {
  const { t } = useTranslation(NS);
  const update = (index: number, patch: Partial<BenchmarkQuoteForm>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <LineSection
      title={t('efs.reports.monthly.setup.benchmark_quotes', {
        defaultValue: 'Benchmark quote rows',
      })}
      addLabel={t('efs.reports.monthly.setup.add_row', { defaultValue: 'Add row' })}
      onAdd={() => onChange([...rows, blankBenchmarkQuote()])}
      disabled={disabled}
    >
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid gap-2 rounded-md border border-efs-border p-3 md:grid-cols-6"
        >
          <TextField
            label={t('efs.reports.monthly.setup.benchmark_label', {
              defaultValue: 'Benchmark label',
            })}
            value={row.benchmark_label}
            disabled={disabled}
            onChange={(value) => update(index, { benchmark_label: value })}
          />
          <SearchField
            label={t('efs.reports.monthly.setup.incoming_item', { defaultValue: 'Incoming item' })}
            value={row.incoming_item_id}
            options={incomingItemOptions}
            disabled={disabled}
            onChange={(value) => update(index, { incoming_item_id: value })}
          />
          <SearchField
            label={t('efs.reports.monthly.setup.material', { defaultValue: 'Material' })}
            value={row.material_id}
            options={materialOptions}
            disabled={disabled}
            onChange={(value) => update(index, { material_id: value })}
          />
          <SelectField
            label={t('efs.reports.monthly.setup.unit', { defaultValue: 'Unit' })}
            value={row.unit}
            disabled={disabled}
            onValueChange={(value) => update(index, { unit: value })}
            options={UNIT_CHOICES.map((unit) => ({
              value: unit,
              label: t(`efs.reports.monthly.setup.unit_${unit}`, { defaultValue: unit }),
            }))}
          />
          <TextField
            label={t('efs.reports.monthly.setup.quoted_unit_price', {
              defaultValue: 'Quoted unit price',
            })}
            value={row.quoted_unit_price}
            disabled={disabled}
            onChange={(value) => update(index, { quoted_unit_price: value })}
          />
          <RemoveButton
            disabled={disabled || rows.length <= 1}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
          <TextField
            label={t('efs.reports.monthly.setup.quote_source_date', {
              defaultValue: 'Quote source date',
            })}
            type="date"
            value={row.quote_source_date}
            disabled={disabled}
            onChange={(value) => update(index, { quote_source_date: value })}
          />
        </div>
      ))}
    </LineSection>
  );
};

const LineSection = ({
  title,
  addLabel,
  onAdd,
  disabled,
  children,
}: {
  title: string;
  addLabel: string;
  onAdd: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between gap-3">
      <h4 className="text-sm font-medium text-efs-text-primary">{title}</h4>
      <RowControls addLabel={addLabel} onAdd={onAdd} disabled={disabled} />
    </div>
    <fieldset disabled={disabled} className="space-y-3">
      {children}
    </fieldset>
  </div>
);

const SearchField = ({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  options: SearchableSelectOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation(NS);
  return (
    <Label className="block space-y-1">
      <span>{label}</span>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        placeholder={t('efs.reports.monthly.setup.no_option', { defaultValue: 'Select an option' })}
        clearLabel={t('efs.common.actions.clear', { ns: 'common', defaultValue: 'Clear' })}
      />
    </Label>
  );
};

const RemoveButton = ({ disabled, onClick }: { disabled?: boolean; onClick: () => void }) => {
  const { t } = useTranslation(NS);
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="mt-6"
      disabled={disabled}
      onClick={onClick}
      aria-label={t('efs.reports.monthly.setup.remove_row', { defaultValue: 'Remove row' })}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
};

export default ReportSettingsCreatePanel;
